/**
 * WhatsApp templates (F3-I): CRUD with variable hints ({{nombre}},
 * {{curso}}, {{monto}}). The body is interpolated in `buildWaLink` when used.
 */
import { createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Plus, Trash2 } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { useCreateTemplate, useDeleteTemplate, useTemplates, useUpdateTemplate } from "./api";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

export function TemplatesView() {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const templates = useTemplates(activeOrgId);
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const remove = useDeleteTemplate();

  const [key, setKey] = createSignal("");
  const [name, setName] = createSignal("");
  const [body, setBody] = createSignal("");

  const add = () => {
    if (!key().trim() || !name().trim()) {
      showToast(t(msg`Key and name are required`), "error");
      return;
    }
    create.mutate(
      { org: activeOrgId(), key: key().trim(), name: name().trim(), body: body() },
      {
        onSuccess: () => {
          showToast(t(msg`Template created`), "success");
          setKey("");
          setName("");
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  return (
    <div class="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
      <div class="space-y-3 rounded-xl border border-border bg-card p-5" data-testid="template-new">
        <h3 class="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          {t(msg`New template`)}
        </h3>
        <input
          class={field}
          value={key()}
          onInput={(e) => setKey(e.currentTarget.value)}
          placeholder={t(msg`Key (e.g. welcome)`)}
          data-testid="template-key"
        />
        <input
          class={field}
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
          placeholder={t(msg`Display name`)}
          data-testid="template-name"
        />
        <textarea
          class={field}
          rows={4}
          value={body()}
          onInput={(e) => setBody(e.currentTarget.value)}
          data-testid="template-body"
          placeholder={t(msg`Message body with variables in double braces`)}
        />
        <p class="text-xs text-muted-foreground">
          {t(
            msg`Variables nombre, curso and monto (each wrapped in double braces) are replaced when the message is sent.`,
          )}
        </p>
        <Button
          class="gap-2"
          onClick={add}
          disabled={create.isPending}
          data-testid="template-create"
        >
          <Plus class="h-4 w-4" /> {t(msg`Create template`)}
        </Button>
      </div>

      <div class="space-y-2">
        <h3 class="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          {t(msg`Templates`)}
        </h3>
        <For
          each={templates.data ?? []}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`No templates yet.`)}</p>}
        >
          {(tpl) => (
            <TemplateRow
              tpl={tpl}
              onSave={(b) => update.mutate({ id: tpl.id, body: b })}
              onDelete={() => remove.mutate(tpl.id)}
            />
          )}
        </For>
      </div>
    </div>
  );
}

function TemplateRow(props: {
  tpl: { id: string; key: string; name: string; body: string };
  onSave: (body: string) => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  // Editable initial value (non-reactive seed of the template body).
  // oxlint-disable-next-line solid/reactivity
  const [body, setBody] = createSignal(props.tpl.body);
  const [dirty, setDirty] = createSignal(false);
  return (
    <div class="rounded-lg border border-border bg-card p-3">
      <div class="flex items-center justify-between">
        <div>
          <span class="font-semibold">{props.tpl.name}</span>
          <span class="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
            {props.tpl.key}
          </span>
        </div>
        <button
          type="button"
          class="text-muted-foreground hover:text-destructive"
          onClick={() => props.onDelete()}
          aria-label={t(msg`Delete template`)}
        >
          <Trash2 class="h-4 w-4" />
        </button>
      </div>
      <textarea
        class={field}
        rows={3}
        value={body()}
        onInput={(e) => {
          setBody(e.currentTarget.value);
          setDirty(true);
        }}
      />
      <Show when={dirty()}>
        <div class="mt-2 flex justify-end">
          <Button
            size="sm"
            onClick={() => {
              props.onSave(body());
              setDirty(false);
            }}
          >
            {t(msg`Save`)}
          </Button>
        </div>
      </Show>
    </div>
  );
}
